import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { Formik } from 'formik';
import AppContainer from '../../../../components/AppContainer';
import PBIcon from '../../../../components/PBIcon';
import DatePickerField from '../../../../components/DatePickerField';
import RecordSops from '../../../../components/RecordSops';
import RecordInstruction from '../../../../components/RecordInstruction';
import AttachRecord from '../../../../components/AttachRecord';
import CustomButton from '../../../../components/CustomButton';
import Preview from '../../../../components/Preview';
import withRouter from '../../../../routes/withRouter';
import moment from 'moment';
import printJS from 'print-js';
import RecordBinderHelper from '@progressivebeef/shared/src/helpers/RecordBinderHelper';
import localization from '@progressivebeef/shared/src/localization/i18n';
import {
  getLocale,
  getToken,
} from '@progressivebeef/shared/src/services/storageService/GlobalData';
import { baseUrl } from '@progressivebeef/shared/src/services/api';
import { globalHelper } from '@progressivebeef/shared/src/helpers';
import APIConstants from '@progressivebeef/shared/src/constants/APIConstants';
import { ATTACHMENT_TYPES } from '@progressivebeef/shared/src/constants/FormConstants';
import RouteConstants from '@progressivebeef/shared/src/constants/RouteConstants';
import {
  showErrorAlert,
  showSuccessAlert,
} from '../../../../components/Alerts';
import {
  addRecordRequest,
  getRecordHistoryRequest,
  getRecordHistoryDetailsRequest,
  updateRecordRequest,
  uploadRecordAttachmentRequested,
  clearRecordAttachmentUpload,
  clearAllRecordAttachmentUploads,
} from '@progressivebeef/shared/src/store/actions/recordBinder';
import {
  showLoader,
  hideLoader,
} from '@progressivebeef/shared/src/store/actions/view';
import { AddRecordValidationSchema } from '@progressivebeef/shared/src/constants/FormValidations';
import './style.scss';

const MAX_ATTACHMENTS = 3;
const PDF_TYPES = [ATTACHMENT_TYPES.PDF, ATTACHMENT_TYPES.PDF_WEB];
const PNG_TYPES = [ATTACHMENT_TYPES.PNG, ATTACHMENT_TYPES.PNG_WEB];

class AddRecord extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      recordBinder: {},
      currentRecord: null,
      instructionsExpanded: false,
      showPreview: false,
      previewSource: null,
      previewType: null,
      isFetchingAttachment: false,
    };
  }

  componentDidMount() {
    const { user } = this.props;

    if (!RecordBinderHelper.canUserWriteRecordBinder(user)) {
      this.props.history.push(RouteConstants.RECORD_BINDER);
      return;
    }

    // Sweep any leftover upload state from a previous Add/Edit session the
    // user abandoned (back/cancel) before its upload(s) resolved.
    this.props.clearAllAttachmentUploads();

    const { currentRecordBinder, currentRecord } =
      this.props.location?.state || {};
    let recordBinder = currentRecordBinder;
    if (!recordBinder) {
      recordBinder = this.props.currentRecordBinder;
    }

    if (recordBinder) {
      this.setState({ recordBinder });
    }

    if (currentRecord && currentRecord.id) {
      this.setState({ currentRecord });
      this.props.getRecordHistoryDetails({ id: currentRecord.id });
    }
  }

  componentDidUpdate(prevProps) {
    const { currentRecord } = this.props.location?.state || {};
    if (
      currentRecord &&
      currentRecord.id &&
      currentRecord.id !== this.state.currentRecord?.id
    ) {
      this.setState({ currentRecord });
      this.props.getRecordHistoryDetails({ id: currentRecord.id });
    }

    let {
      isHistoryDetailsLoading,
      isSubmitting,
      isUpdating,
      showLoaderView,
      hideLoaderView,
      error,
      success,
      updateSuccess,
    } = this.props;
    const isLoading =
      isHistoryDetailsLoading ||
      isSubmitting ||
      isUpdating ||
      this.state.isFetchingAttachment;

    if (isLoading) {
      showLoaderView();
    } else {
      hideLoaderView();
    }

    if (error && error !== prevProps.error && Object.keys(error).length !== 0) {
      showErrorAlert(error && (error.message || error), [
        {
          text: localization[getLocale()].OK,
          onPress: () => {},
        },
      ]);
    }

    if (success && success !== prevProps.success && success) {
      showSuccessAlert(localization[getLocale()].RECORD_ADDED_SUCCESSFULLY, [
        {
          text: localization[getLocale()].OK,
          onPress: () => {
            const { recordBinder } = this.state;
            if (recordBinder && recordBinder.id) {
              this.props.getRecordHistory({ recordId: recordBinder.id });
            }
            this.props.history.goBack();
          },
        },
      ]);
    }

    if (
      updateSuccess &&
      updateSuccess !== prevProps.updateSuccess &&
      updateSuccess
    ) {
      showSuccessAlert(localization[getLocale()].RECORD_UPDATED_SUCCESSFULLY, [
        {
          text: localization[getLocale()].OK,
          onPress: () => {
            const { recordBinder } = this.state;
            if (recordBinder && recordBinder.id) {
              this.props.getRecordHistory({ recordId: recordBinder.id });
            }
            this.props.history.goBack();
          },
        },
      ]);
    }

    // Pick up newly-settled attachment uploads (dispatched in
    // handleFilesAdded) and fold each result into Formik's array state.
    const attachmentUploads = this.props.attachmentUploads || {};
    const prevAttachmentUploads = prevProps.attachmentUploads || {};
    Object.keys(attachmentUploads).forEach((tempId) => {
      const current = attachmentUploads[tempId];
      const previous = prevAttachmentUploads[tempId];
      const justSettled =
        current &&
        current.status !== 'uploading' &&
        (!previous || previous.status === 'uploading');
      if (justSettled) {
        this.handleFileUploadSettled(
          tempId,
          current.attachmentId || null,
          current.status === 'failure',
        );
        // Housekeeping: this result has been consumed, don't let it linger
        // in the store.
        this.props.clearAttachmentUpload({ tempId });
      }
    });
  }

  handleFilesAdded = (newFiles, values, setFieldValue) => {
    const currentAttachments = values.attachments || [];
    const remainingSlots = MAX_ATTACHMENTS - currentAttachments.length;
    let filesToAdd = newFiles;

    if (newFiles.length > remainingSlots) {
      showErrorAlert(localization[getLocale()].ATTACHMENT_LIMIT_REACHED);
      filesToAdd = newFiles.slice(0, remainingSlots);
    }

    if (filesToAdd.length === 0) {
      return;
    }

    const { recordBinder } = this.state;
    const pendingAttachments = filesToAdd.map((file) => ({
      ...file,
      tempId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      isUploading: true,
      attachmentId: null,
    }));

    setFieldValue('attachments', [
      ...currentAttachments,
      ...pendingAttachments,
    ]);

    // Dispatch the upload the moment each file is picked, rather than at
    // submit time - by the time the user hits Save, attachmentId is already
    // resolved and the save request no longer needs to upload anything
    pendingAttachments.forEach((attachment) => {
      this.props.uploadRecordAttachment({
        tempId: attachment.tempId,
        fileName: attachment.name,
        data: attachment.base64,
        extension: attachment.extension,
        feedlotRecordId: String(recordBinder.id),
      });
    });
  };

  // Bridges a settled upload result back to Formik's array state.
  handleFileUploadSettled = (tempId, attachmentId, didFail) => {
    const setFieldValue = this.formikSetFieldValue;
    if (!setFieldValue) {
      return;
    }
    const currentAttachments = this.formikValues?.attachments || [];

    if (didFail) {
      showErrorAlert(localization[getLocale()].SOMETHING_WENT_WRONG);
      setFieldValue(
        'attachments',
        currentAttachments.filter((attachment) => attachment.tempId !== tempId),
      );
      return;
    }

    setFieldValue(
      'attachments',
      currentAttachments.map((attachment) =>
        attachment.tempId === tempId
          ? { ...attachment, isUploading: false, attachmentId }
          : attachment,
      ),
    );
  };

  handleFileRemove = (index, values, setFieldValue) => {
    const currentAttachments = values.attachments || [];
    setFieldValue(
      'attachments',
      currentAttachments.filter((_, i) => i !== index),
    );
  };

  toggleInstructions = () => {
    this.setState({ instructionsExpanded: !this.state.instructionsExpanded });
  };

  closePreview = () => {
    // Invalidate any in-flight fetch so it can't reopen the modal if it
    // resolves after the user has already closed it.
    this.currentAttachmentPreviewId = null;
    this.setState({
      showPreview: false,
      previewSource: null,
      previewType: null,
    });
  };

  handlePrint = () => {
    const { previewSource, previewType } = this.state;
    if (!previewSource) {
      return;
    }

    if (PDF_TYPES.includes(previewType)) {
      RecordBinderHelper.printPDF(previewSource, printJS, (error) => {
        showErrorAlert('Failed to print document');
      });
    } else {
      const imageType = PNG_TYPES.includes(previewType)
        ? ATTACHMENT_TYPES.PNG
        : ATTACHMENT_TYPES.JPEG;
      RecordBinderHelper.printImage(
        previewSource,
        imageType,
        printJS,
        (error) => {
          showErrorAlert('Failed to print document');
        },
      );
    }
  };

  // Lets the user see what they're about to edit or remove - a single-item
  // preview, no prev/next navigation (that's reserved for the read-only
  // View Document flow in RecordBinderDetail).
  handleFilePreview = async (attachment) => {
    if (!attachment) {
      return;
    }

    const rawType = (
      attachment.fileType ||
      attachment.extension ||
      ''
    ).toLowerCase();
    const previewType =
      rawType === ATTACHMENT_TYPES.PDF
        ? ATTACHMENT_TYPES.PDF
        : rawType === ATTACHMENT_TYPES.JPG || rawType === ATTACHMENT_TYPES.JPEG
          ? ATTACHMENT_TYPES.JPEG
          : ATTACHMENT_TYPES.PNG;

    // Freshly-picked attachments already have the content in memory -
    // preview immediately. Clear any in-flight remote fetch's claim so it
    // can't land after this and overwrite it.
    if (attachment.base64) {
      this.currentAttachmentPreviewId = null;
      this.setState({
        showPreview: true,
        previewSource: attachment.base64,
        previewType,
      });
      return;
    }

    // Saved attachments only carry a reference (attachmentId) - fetch the
    // actual bytes first.
    if (!attachment.attachmentId) {
      showErrorAlert(localization[getLocale()].ATTACHMENT_REQUIRED);
      return;
    }

    // Guards against a stale fetch landing late: if the user taps another
    // row before this one resolves, only the most recently requested
    // attachmentId is allowed to apply its result.
    const requestId = attachment.attachmentId;
    this.currentAttachmentPreviewId = requestId;

    this.setState({ isFetchingAttachment: true });

    if (!navigator.onLine) {
      this.setState({ isFetchingAttachment: false });
      showErrorAlert(localization[getLocale()].NO_INTERNET_CONNECTION);
      return;
    }

    try {
      const uri = `${baseUrl}${APIConstants.GET_ATTACHMENT}`.replace(
        '{{id}}',
        attachment.attachmentId,
      );
      const base64 = await globalHelper.getFileFromServer(uri, {
        token: getToken(),
      });
      if (this.currentAttachmentPreviewId !== requestId) {
        return;
      }
      this.setState({
        isFetchingAttachment: false,
        showPreview: true,
        previewSource: base64,
        previewType,
      });
    } catch (error) {
      if (this.currentAttachmentPreviewId !== requestId) {
        return;
      }
      this.setState({ isFetchingAttachment: false });
      showErrorAlert(
        localization[getLocale()].SOMETHING_WENT_WRONG ||
          'Something went wrong',
      );
    }
  };

  handleSubmit = (values, formikActions) => {
    const { recordBinder, currentRecord } = this.state;

    if (!navigator.onLine) {
      showErrorAlert(localization[getLocale()].NO_INTERNET_CONNECTION);
      return;
    }

    const hasUploadingAttachment = (values.attachments || []).some(
      (attachment) => attachment.isUploading,
    );
    if (hasUploadingAttachment) {
      showErrorAlert(localization[getLocale()].ATTACHMENT_STILL_UPLOADING);
      return;
    }

    // Every attachment is uploaded as soon as it's picked (handleFilesAdded),
    // so by the time this runs each one already carries a resolved
    // attachmentId - nothing left to upload here.
    const attachmentIds = (values.attachments || [])
      .map((attachment) => attachment.attachmentId)
      .filter(Boolean);

    const payload = {
      FeedlotRecordHistoryDTO: {
        feedlotRecordId: String(recordBinder.id),
        expiresOn: moment(values.expiresOn).endOf('day').utc().toString(),
        attachmentIds,
      },
    };

    if (currentRecord && currentRecord.id) {
      payload.FeedlotRecordHistoryDTO.id = currentRecord.id;
      this.props.updateRecord({
        id: currentRecord.id,
        ...payload,
      });
    } else {
      this.props.addRecord(payload);
    }
  };

  renderLeftHeader = () => {
    return (
      <div className="head-icon left-align">
        <PBIcon
          name={'chevron-left'}
          onPress={() => {
            this.props.history.goBack();
          }}
        />
      </div>
    );
  };

  renderPreviewLeftHeader = () => {
    return (
      <div className="head-icon left-align">
        <PBIcon name={'chevron-left'} onPress={this.closePreview} />
      </div>
    );
  };

  render() {
    const {
      recordBinder,
      currentRecord,
      showPreview,
      previewSource,
      previewType,
    } = this.state;
    const { isSubmitting, isUpdating, recordHistoryDetails } = this.props;

    const initialValues = RecordBinderHelper.getInitialValues(
      currentRecord,
      recordHistoryDetails,
      recordBinder,
    );
    const isEditMode = currentRecord && currentRecord.id;

    const title = isEditMode
      ? localization[getLocale()].EDIT_RECORD || 'Edit Record'
      : localization[getLocale()].ADD_RECORD || 'Add Record';

    const isPreviewPDF = PDF_TYPES.includes(previewType);
    const previewImageType = PNG_TYPES.includes(previewType)
      ? ATTACHMENT_TYPES.PNG
      : ATTACHMENT_TYPES.JPEG;

    return (
      <>
        <AppContainer
          title={title}
          iconHeader
          IconLeftHeader={this.renderLeftHeader}
          noncollapsible
          history={this.props.history}>
          <Formik
            key={
              currentRecord?.id
                ? `edit-${currentRecord.id}-${recordHistoryDetails ? 'loaded' : 'loading'}`
                : 'add'
            }
            initialValues={initialValues}
            enableReinitialize={true}
            onSubmit={this.handleSubmit}
            validationSchema={AddRecordValidationSchema}
            validateOnMount={false}>
            {({
              values,
              setFieldValue,
              errors,
              submitCount,
              isValid,
              handleSubmit,
            }) => {
              // Stashed so handleFileUploadSettled (fired later, from an
              // async upload callback) can read current Formik state without
              // closing over a stale render.
              this.formikValues = values;
              this.formikSetFieldValue = setFieldValue;

              const isSubmitted = submitCount > 0;
              const hasUploadingAttachment = (values.attachments || []).some(
                (attachment) => attachment.isUploading,
              );
              const isLoading = isSubmitting || isUpdating;

              return (
                <>
                  <div className="add-record-container">
                    <div className="record-details-section">
                      <h3 className="record-details-title">
                        {localization[getLocale()].RECORD_DETAILS ||
                          'Record Details'}
                      </h3>
                      <RecordSops sops={recordBinder?.sops} />
                      <RecordInstruction
                        instructions={recordBinder?.instructions}
                        expanded={this.state.instructionsExpanded}
                        onToggle={this.toggleInstructions}
                        truncate={RecordBinderHelper.truncateInstructions}
                        customStyle={{ backgroundColor: '#FCFBFE' }}
                      />
                      <div className="record-details-divider"></div>
                    </div>

                    <div className="add-record-form">
                      <div className="form-field">
                        <AttachRecord
                          attachments={values.attachments || []}
                          onFilesAdded={(files) =>
                            this.handleFilesAdded(files, values, setFieldValue)
                          }
                          onFileRemove={(index) =>
                            this.handleFileRemove(index, values, setFieldValue)
                          }
                          onFilePreview={this.handleFilePreview}
                          hasError={
                            isSubmitted && errors.attachments ? true : false
                          }
                        />
                        {isSubmitted && errors.attachments && (
                          <div className="form-error">
                            {typeof errors.attachments === 'string'
                              ? errors.attachments
                              : localization[getLocale()].ATTACHMENT_REQUIRED}
                          </div>
                        )}
                      </div>

                      <div className="form-field">
                        <label className="form-label">
                          {localization[getLocale()].EXPIRES_ON_TITLE}
                          <span className="required"> *</span>
                        </label>
                        <DatePickerField
                          value={values.expiresOn}
                          onChange={(date) => setFieldValue('expiresOn', date)}
                          minimumDate={(() => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            return today;
                          })()}
                          maximumDate={(() => {
                            const maxDate = new Date();
                            maxDate.setFullYear(maxDate.getFullYear() + 10);
                            return maxDate;
                          })()}
                          placement="top"
                          placeholder={localization[getLocale()].SELECT_DATE}
                          hasError={
                            isSubmitted && errors.expiresOn ? true : false
                          }
                          showMonthYearDropdown
                        />
                        {isSubmitted && errors.expiresOn && (
                          <div className="form-error">{errors.expiresOn}</div>
                        )}
                      </div>

                      <div className="add-record-footer">
                        <div className="add-record-footer-buttons">
                          <CustomButton
                            title={localization[getLocale()].CANCEL}
                            onPressEvent={() => this.props.history.goBack()}
                            buttonColor="white"
                            textColor="#635F5F"
                            customBtnStyles={{
                              width: '120px',
                              padding: '8px',
                              border: '1px solid #DADADA',
                            }}
                            customTextStyles={{
                              fontSize: 14,
                              fontWeight: 600,
                            }}
                          />
                          <CustomButton
                            title={localization[getLocale()].SAVE || 'Save'}
                            onPressEvent={(e) => {
                              e.preventDefault();

                              if (hasUploadingAttachment) {
                                showErrorAlert(
                                  localization[getLocale()]
                                    .ATTACHMENT_STILL_UPLOADING,
                                );
                                return;
                              }
                              if (!isValid) {
                                showErrorAlert(
                                  localization[getLocale()]
                                    .PLEASE_FILL_ALL_FIELDS ||
                                    'Please fill all required fields',
                                );
                                return;
                              }
                              handleSubmit();
                            }}
                            disabled={isLoading || hasUploadingAttachment}
                            buttonColor="#32076e"
                            textColor="#fff"
                            customBtnStyles={{
                              width: '120px',
                              padding: '8px',
                            }}
                            customTextStyles={{
                              fontSize: 14,
                              fontWeight: 600,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              );
            }}
          </Formik>

          {showPreview && (
            <Preview
              modalVisible={showPreview}
              leftHeader
              title={
                isPreviewPDF
                  ? localization[getLocale()].PDF_PREVIEW
                  : localization[getLocale()].IMAGE_PREVIEW
              }
              pdfSource={isPreviewPDF ? previewSource : null}
              imageSource={!isPreviewPDF ? previewSource : null}
              type={!isPreviewPDF ? previewImageType : null}
              isBase64={true}
              isComment={false}
              IconLeftHeader={this.renderPreviewLeftHeader}
              history={this.props.history}
            />
          )}
        </AppContainer>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  currentRecordBinder: state.recordBinder.currentRecordBinder,
  isSubmitting: state.recordBinder.isSubmitting,
  isUpdating: state.recordBinder.isUpdating,
  error: state.recordBinder.error,
  success: state.recordBinder.success,
  updateSuccess: state.recordBinder.updateSuccess,
  recordHistoryDetails: state.recordBinder.recordHistoryDetails,
  isHistoryDetailsLoading: state.recordBinder.isHistoryDetailsLoading,
  attachmentUploads: state.recordBinder.attachmentUploads,
  user: state.authentication.user,
});

const mapDispatchToProps = (dispatch) => {
  return {
    addRecord: (payload) => dispatch(addRecordRequest(payload)),
    updateRecord: (payload) => dispatch(updateRecordRequest(payload)),
    getRecordHistory: (payload) => dispatch(getRecordHistoryRequest(payload)),
    getRecordHistoryDetails: (payload) =>
      dispatch(getRecordHistoryDetailsRequest(payload)),
    uploadRecordAttachment: (payload) =>
      dispatch(uploadRecordAttachmentRequested(payload)),
    clearAttachmentUpload: (payload) =>
      dispatch(clearRecordAttachmentUpload(payload)),
    clearAllAttachmentUploads: () =>
      dispatch(clearAllRecordAttachmentUploads()),
    showLoaderView: (payload) => dispatch(showLoader(payload)),
    hideLoaderView: (payload) => dispatch(hideLoader(payload)),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(AddRecord));
